import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';
import mixpanel from 'mixpanel-browser';

import {
  Grid,
  Modal,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from '@material-ui/core';
import {
  MobileStepper,
  Box,
  FormControl,
  Input,
  InputLabel,
  InputAdornment,
  Alert,
} from '@mui/material';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';

import {
  InvestmentSimulationWizardInputKeys,
  exchangeRates,
  COLOR_GREY,
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_PRIMARY,
  COLOR_FORM_BACKGROUND,
  COLOR_WHITE_ACCENT,
} from 'src/const';
import { ROICalculatorModalPropTypes } from 'src/interfaces/iproptypes';
import {
  ROICalculatorOptionalInputFieldsType,
  ADR,
  OccupancyRate,
  ROICalculatorInputFieldValues,
  TempROICalculatorInputFieldValues,
} from 'src/interfaces/idashboard';
import { convertCurrency, formatCurrency } from 'src/utils/functions';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStyles = makeStyles((theme: Theme) => ({
  paperModal: {
    position: 'absolute',
    background: COLOR_FORM_BACKGROUND,
    width: '60%',
    height: 600,
    borderRadius: 20,
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down(550)]: {
      overflow: 'scroll',
      width: '90%',
    },
  },
  headerContainer: {
    width: '100%',
    background: COLOR_PRIMARY,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  header: {
    fontSize: '1.5rem',
    textAlign: 'center',
    color: '#fff',
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
    paddingTop: 20,
    paddingBottom: 20,
    [theme.breakpoints.down(900)]: {
      fontSize: '1rem',
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
  },
  bodyContainer: {
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
    [theme.breakpoints.down(900)]: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
  },
  question: {
    fontSize: '1.5rem',
    color: '#fff',
    [theme.breakpoints.down(900)]: {
      fontSize: '1rem',
    },
  },
  hint: {
    marginTop: 10,
    fontSize: '1.2rem',
    color: COLOR_WHITE_ACCENT,
    [theme.breakpoints.down(900)]: {
      fontSize: '0.8rem',
    },
  },
  stepNumber: {
    display: 'inline',
    fontSize: '1rem',
    marginRight: 10,
    [theme.breakpoints.down(900)]: {
      fontSize: '0.8rem',
    },
  },
  stepperContainer: {
    marginBottom: 40,
    [theme.breakpoints.down(900)]: {
      marginBottom: 20,
    },
  },
  button: {
    background: '#fff',
    borderRadius: 5,
    border: 0,
    color: COLOR_GREY,
    padding: '5px 30px',
    '&:hover': {
      background: '#fff',
      color: COLOR_BLUE_BLACK_LIGHT,
    },
    fontSize: 16,
    [theme.breakpoints.down(900)]: {
      fontSize: '1rem',
      padding: '5px 10px',
      marginLeft: 10,
    },
    marginTop: 10,
    marginRight: 10,
  },
  submitButton: {
    background: '#fff',
    borderRadius: 5,
    border: 0,
    color: COLOR_GREY,
    padding: '5px 30px',
    '&:hover': {
      background: '#fff',
      color: COLOR_BLUE_BLACK_LIGHT,
    },
    fontSize: '1rem',
    marginTop: 20,
    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down(900)]: {
      padding: '5px 10px',
      fontSize: '1rem',
    },
  },
}));

const formTheme = createTheme({
  palette: {
    secondary: {
      main: '#fff',
    },
  },
});

const SimulatorWizardModal = ({
  simulatorModalOpen,
  setSimulatorModalOpen,
  currency,
  matches,
  setInvestmentCalInputVals,
  simulateROI,
}: ROICalculatorModalPropTypes) => {
  const classes = useStyles();
  const theme = useTheme();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [activeStepperStep, setActiveStepperStep] = useState<number>(0);
  const maxSteps: number = InvestmentSimulationWizardInputKeys.length;
  const [
    investmentSimulationWizardInputValues,
    setInvestmentSimulationWizardInputValues,
  ] = useState<TempROICalculatorInputFieldValues>({
    propertyPrice: '',
    downPayment: '',
    managementFee: '',
    propertyTax: '',
    lodgingTax: '',
    mortgageFees: '',
    hoaFees: '0',
    maintenance: '',
    insurance: '',
    electricity: '',
    wifi: '',
    gas: '',
    other: '',
    homeFurnishings: '',
    closingCosts: '',
    takingLoan: '0',
    loanTerm: '30',
    interestRate: '7',
  });
  

  const [isRenderingOptionalInputFields, setIsRenderingOptionalInputFields] = useState<
    ROICalculatorOptionalInputFieldsType
  >({
    willUseManagementCompany: false,
  });
  const [inputValidationAlertTriggered, setInputValidationAlertTriggered] = useState<boolean>(
    false
  );
  const [inputValidationAlertMessage, setInputValidationAlertMessage] = useState<string>('');

  useEffect(() => {
    const cachedSimulatorInputs = localStorage.getItem('cachedSimulatorInputs');
    if (cachedSimulatorInputs) {
      const cachedSimulatorInputsObj = JSON.parse(cachedSimulatorInputs);
      Object.keys(cachedSimulatorInputsObj).forEach(key => {
        cachedSimulatorInputsObj[key] = cachedSimulatorInputsObj[key].toString();
      });
      setInvestmentSimulationWizardInputValues({
        ...investmentSimulationWizardInputValues,
        ...cachedSimulatorInputsObj,
      });
    }
  }, []);

  const handleInputFieldValueChange = (key: string, event) => {
    setInvestmentSimulationWizardInputValues({
      ...investmentSimulationWizardInputValues,
      [key]: event.target.value.replaceAll(',', ''),
    });
  };

  const handleNext = (): void => {
    mixpanel.track('simulatorWizardNextButtonClick');
    const key = InvestmentSimulationWizardInputKeys[activeStep].key;
    const value = investmentSimulationWizardInputValues[key];
    
    if (value === '') {
      if (InvestmentSimulationWizardInputKeys[activeStep].required) {
        setInputValidationAlertMessage('Please enter a value');
        setInputValidationAlertTriggered(true);
        return;
      }
      setInvestmentSimulationWizardInputValues({
        ...investmentSimulationWizardInputValues,
        [key]: '0',
      });
    }
    const parsedValue = parseFloat(value);
    if (
      (key === 'managementFee' || key === 'lodgingTax' || key === 'propertyTax') &&
      (parsedValue > 100 || parsedValue < 0)
    ) {
      setInputValidationAlertMessage('Please enter a number between 0 and 100');
      setInputValidationAlertTriggered(true);
      return;
    } else if (parsedValue < 0) {
      setInputValidationAlertMessage('Please enter a number greater than 0');
      setInputValidationAlertTriggered(true);
      return;
    } else {
      setInputValidationAlertTriggered(false);
      setActiveStep((prevActiveStep: number) => {
        

        if (key === 'takingLoan' && value === '0') {
          setInvestmentSimulationWizardInputValues({
            ...investmentSimulationWizardInputValues,
            ['downPayment']: investmentSimulationWizardInputValues['propertyPrice'],
          });
          setActiveStepperStep(InvestmentSimulationWizardInputKeys[6].step - 1);
          return 6;
        }

        if (key === 'takingLoan' && value === '1') {
          setInvestmentSimulationWizardInputValues({
            ...investmentSimulationWizardInputValues,
            [key]: '1',
          });
        }

        if (key === 'propertyPrice') {
          setInvestmentSimulationWizardInputValues({
            ...investmentSimulationWizardInputValues,
            ['downPayment']: (parseInt(value) / 100) * 20 + '',
          });
        }

        let nextStep = prevActiveStep + 1;
        for (let i = nextStep; i < InvestmentSimulationWizardInputKeys.length; i++) {
          if (!InvestmentSimulationWizardInputKeys[i].hide) {
            setActiveStepperStep(InvestmentSimulationWizardInputKeys[nextStep].step - 1);
            return nextStep;
          }
          nextStep++;
          if (nextStep === InvestmentSimulationWizardInputKeys.length) {
            return prevActiveStep;
          }
        }
        setActiveStepperStep(InvestmentSimulationWizardInputKeys[nextStep].step - 1);
        return nextStep;
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();
      setActiveStep((prevActiveStep: number) => {
        if (prevActiveStep < maxSteps - 1) {
          handleNext();
        } else {
          syncInputFieldValues();
        }
        return prevActiveStep;
      });
    }
  };

  const handleBack = (): void => {
    mixpanel.track('simulatorWizardBackButtonClick');
    setActiveStep((prevActiveStep: number) => {
      let previousStep = prevActiveStep - 1;
      for (let i = previousStep; i >= 0; i--) {
        if (!InvestmentSimulationWizardInputKeys[i].hide) {
          setActiveStepperStep(InvestmentSimulationWizardInputKeys[previousStep].step - 1);
          return previousStep;
        }
        previousStep--;
        if (previousStep === 0) {
          return prevActiveStep;
        }
      }
      setActiveStepperStep(InvestmentSimulationWizardInputKeys[previousStep].step - 1);
      return previousStep;
    });
  };

  const handleBooleanValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
    inputField: string
  ): void => {
    
    if (inputField === 'loanTerm') {
      setInvestmentSimulationWizardInputValues({
        ...investmentSimulationWizardInputValues,
        ['loanTerm']: value,
      });
    }

    if (inputField === 'takingLoan') {
      setInvestmentSimulationWizardInputValues({
        ...investmentSimulationWizardInputValues,
        ['takingLoan']: value === 'Yes' ? '1' : '0',
      });
    }

    setIsRenderingOptionalInputFields({
      ...isRenderingOptionalInputFields,
      [inputField]: value === 'Yes' ? true : false,
    });
  };

  useEffect(() => {
    InvestmentSimulationWizardInputKeys[13].hide = !isRenderingOptionalInputFields.willUseManagementCompany;
  }, [isRenderingOptionalInputFields]);

  const parseQuestion = (question: string): string => {
    const parsedQuestion = question.replace(
      '@MN',
      `${exchangeRates[currency].symbol +
        convertCurrency(
          currency,
          parseFloat(InvestmentSimulationWizardInputKeys[activeStep].placeholder as string)
        )}`
    );
    const boldRegex = /(?:\*)(?:(?!\s))((?:(?!\*|\n).)+)(?:\*)/g;
    const underlineRegex = /(?:\_)(?:(?!\s))((?:(?!\_|\n).)+)(?:\_)/g;
    const linkRegex = /\[(.*?)\]\((.*?)\)/gi;
    const boldParsedQuestion = parsedQuestion
      .replace(boldRegex, `<div style="display: inline; font-weight: bold">$1</div>`)
      .replace(underlineRegex, `<div style="display: inline; text-decoration: underline">$1</div>`)
      .replace(linkRegex, `<a href="$2" target="_blank" style="text-decoration: underline">$1</a>`);

    return boldParsedQuestion;
  };

  const syncInputFieldValues = (): void => {
    mixpanel.track('simulatorWizardSubmitClick');
    Object.keys(investmentSimulationWizardInputValues).forEach(key => {
      const value = parseFloat(investmentSimulationWizardInputValues[key]);
      setInvestmentCalInputVals((prevROICalInputFieldValues: ROICalculatorInputFieldValues) => {
        return {
          ...prevROICalInputFieldValues,
          [key]: value === NaN ? 0 : value,
        };
      });
    });
    simulateROI();
    setInvestmentSimulationWizardInputValues({
      propertyPrice: '',
      downPayment: '',
      managementFee: '',
      propertyTax: '',
      lodgingTax: '',
      mortgageFees: '',
      hoaFees: '0',
      maintenance: '',
      insurance: '',
      electricity: '',
      wifi: '',
      gas: '',
      other: '',
      homeFurnishings: '',
      closingCosts: '',
      takingLoan: '0',
      loanTerm: '30',
      interestRate: '7',
    });
    setActiveStep(0);
  };

  return (
    <Modal
      open={simulatorModalOpen}
      onClose={() => setSimulatorModalOpen(false)}
      aria-labelledby="calculator-modal"
      aria-describedby="calculator-modal"
    >
      <Grid
        container
        direction="column"
        alignItems="center"
        justify="center"
        style={{ minHeight: '100vh' }}
        onKeyPress={handleKeyPress}
      >
        <div className={`${classes.paperModal} modal-custom`}>
          <div style={{ position: 'absolute', top: 0, right: 5, zIndex: 100 }}>
            <IconButton>
              <CloseIcon
                style={{ color: 'fff', fontSize: 40 }}
                onClick={() => {
                  setSimulatorModalOpen(false);
                  setActiveStep(0);
                }}
              />
            </IconButton>
          </div>

          <Grid
            container
            direction="column"
            justify="space-between"
            alignItems="center"
            style={{ height: '100%' }}
          >
            {InvestmentSimulationWizardInputKeys[activeStep].header && (
              <Grid item className={classes.headerContainer}>
                <Typography className={classes.header}>
                  <Box className={classes.stepNumber}>
                    {InvestmentSimulationWizardInputKeys[activeStep].step}
                    {'->'}
                  </Box>{' '}
                  {parse(parseQuestion(InvestmentSimulationWizardInputKeys[activeStep].header))}
                </Typography>
              </Grid>
            )}

            {activeStep !== maxSteps - 1 ? (
              <Grid
                item
                className={classes.bodyContainer}
                style={{
                  marginTop: InvestmentSimulationWizardInputKeys[activeStep].header ? 0 : '20%',
                }}
              >
                {/* Question */}
                <Typography className={classes.question}>
                  <Box className={classes.stepNumber}>
                    {InvestmentSimulationWizardInputKeys[activeStep].substep}
                    {InvestmentSimulationWizardInputKeys[activeStep].header ? '.' : '->'}
                  </Box>{' '}
                  {parse(parseQuestion(InvestmentSimulationWizardInputKeys[activeStep].question))}{' '}
                  {InvestmentSimulationWizardInputKeys[activeStep].required && '*'}
                </Typography>

                {/* Hint */}
                {InvestmentSimulationWizardInputKeys[activeStep].hint && (
                  <Typography className={classes.hint}>
                    {parse(parseQuestion(InvestmentSimulationWizardInputKeys[activeStep].hint))}
                  </Typography>
                )}

                {/* Input */}
                {InvestmentSimulationWizardInputKeys[activeStep].inputType === 'number' && (
                  <ThemeProvider theme={formTheme}>
                    <FormControl
                      variant="standard"
                      fullWidth
                      color="secondary"
                      margin="dense"
                      size="medium"
                      sx={{
                        '& .MuiInput-underline:before': {
                          borderBottomColor: COLOR_WHITE_ACCENT,
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          borderBottomColor: COLOR_WHITE_ACCENT,
                        },
                      }}
                    >
                      <Input
                        sx={{
                          color: '#fff',
                          fontSize: '3rem',
                          '& .MuiTypography-root': {
                            color: '#fff',
                            fontSize: '3rem',
                          },
                        }}
                        value={formatCurrency(
                          investmentSimulationWizardInputValues[
                            InvestmentSimulationWizardInputKeys[activeStep].key
                          ]
                        )}
                        placeholder={
                          InvestmentSimulationWizardInputKeys[activeStep].key === 'closingCosts'
                            ? `${Math.round(
                                parseFloat(investmentSimulationWizardInputValues.propertyPrice) *
                                  0.03
                              )}`
                            : InvestmentSimulationWizardInputKeys[activeStep].key ===
                                'electricity' ||
                              InvestmentSimulationWizardInputKeys[activeStep].key === 'wifi' ||
                              InvestmentSimulationWizardInputKeys[activeStep].key === 'gas'
                            ? `${convertCurrency(
                                currency,
                                parseFloat(
                                  InvestmentSimulationWizardInputKeys[activeStep]
                                    .placeholder as string
                                )
                              )}`
                            : InvestmentSimulationWizardInputKeys[activeStep].placeholder
                        }
                        onChange={event =>
                          handleInputFieldValueChange(
                            InvestmentSimulationWizardInputKeys[activeStep].key,
                            event
                          )
                        }
                        type="tel"
                        autoFocus={true}
                        startAdornment={
                          InvestmentSimulationWizardInputKeys[activeStep].unit === '$' && (
                            <InputAdornment position="start">
                              {exchangeRates[currency].symbol}
                            </InputAdornment>
                          )
                        }
                        endAdornment={
                          InvestmentSimulationWizardInputKeys[activeStep].unit !== '$' && (
                            <InputAdornment position="end">
                              {InvestmentSimulationWizardInputKeys[activeStep].unit}
                            </InputAdornment>
                          )
                        }
                      />
                    </FormControl>
                  </ThemeProvider>
                )}

                {InvestmentSimulationWizardInputKeys[activeStep].inputType === 'boolean' && (
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    row
                    style={{ marginTop: 30 }}
                    value={
                      isRenderingOptionalInputFields[
                        InvestmentSimulationWizardInputKeys[activeStep].key
                      ]
                        ? 'Yes'
                        : 'No'
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) =>
                      handleBooleanValueChange(
                        event,
                        value,
                        InvestmentSimulationWizardInputKeys[activeStep].key
                      )
                    }
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="No"
                    />
                  </RadioGroup>
                )}

                {InvestmentSimulationWizardInputKeys[activeStep].inputType === 'loanTerm' && (
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    row
                    style={{ marginTop: 30 }}
                    value={
                      investmentSimulationWizardInputValues[InvestmentSimulationWizardInputKeys[activeStep].key]
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) =>
                      handleBooleanValueChange(
                        event,
                        value,
                        InvestmentSimulationWizardInputKeys[activeStep].key
                      )
                    }
                  >
                    <FormControlLabel
                      value="30"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="30 Years"
                    />
                    <FormControlLabel
                      value="15"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="15 Years"
                    />
                    <FormControlLabel
                      value="10"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="10 Years"
                    />
                  </RadioGroup>
                )}

                {activeStep !== maxSteps - 1 && (
                  <Grid container item direction="row" justify="flex-start" alignItems="center">
                    <Button className={classes.button} onClick={handleNext}>
                      {InvestmentSimulationWizardInputKeys[activeStep].inputType === 'step'
                        ? 'Continue'
                        : 'Ok'}
                    </Button>

                    <Typography
                      variant="caption"
                      style={{
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 10,
                      }}
                    >
                      press{' '}
                      <Box style={{ display: 'inline', fontWeight: 'bold', margin: '0 5px' }}>
                        Enter
                      </Box>{' '}
                      <KeyboardReturnIcon />
                    </Typography>

                    {inputValidationAlertTriggered && (
                      <Alert severity="error" style={{ marginTop: 10, marginLeft: 20 }}>
                        {inputValidationAlertMessage}
                      </Alert>
                    )}
                  </Grid>
                )}
              </Grid>
            ) : (
              <Grid item container spacing={2} className={classes.bodyContainer}>
                {InvestmentSimulationWizardInputKeys.map((summaryField, summaryFieldIndex) => {
                  if (summaryField.inputType === 'number') {
                    return (
                      <Grid item xs={4} md={3} lg={3} key={summaryFieldIndex}>
                        <ThemeProvider theme={formTheme}>
                          <FormControl
                            variant="standard"
                            fullWidth
                            color="secondary"
                            margin="dense"
                            size="small"
                            sx={{
                              '& .MuiInput-underline:before': {
                                borderBottomColor: COLOR_WHITE_ACCENT,
                              },
                              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                borderBottomColor: COLOR_WHITE_ACCENT,
                              },
                            }}
                          >
                            <InputLabel htmlFor={summaryField.key}>{summaryField.label}</InputLabel>
                            <Input
                              id={summaryField.key}
                              sx={{
                                fontSize: '1.5rem',
                                color: '#fff',
                                '& .MuiTypography-root': {
                                  fontSize: '1.5rem',
                                  color: '#fff',
                                  [theme.breakpoints.down(900)]: {
                                    fontSize: '1rem',
                                  },
                                },
                                [theme.breakpoints.down(900)]: {
                                  fontSize: '1rem',
                                },
                              }}
                              value={formatCurrency(
                                investmentSimulationWizardInputValues[summaryField.key]
                              )}
                              onChange={event =>
                                handleInputFieldValueChange(summaryField.key, event)
                              }
                              type="tel"
                              autoFocus={true}
                              startAdornment={
                                summaryField.unit === '$' && (
                                  <InputAdornment position="start">
                                    {exchangeRates[currency].symbol}
                                  </InputAdornment>
                                )
                              }
                              endAdornment={
                                summaryField.unit !== '$' && (
                                  <InputAdornment position="end">
                                    {summaryField.unit}
                                  </InputAdornment>
                                )
                              }
                            />
                          </FormControl>
                        </ThemeProvider>
                      </Grid>
                    );
                  } else {
                    return;
                  }
                })}
              </Grid>
            )}

            {activeStep === maxSteps - 1 && (
              <Grid item>
                <Button className={classes.submitButton} onClick={() => syncInputFieldValues()}>
                  Simulate ROI 🚀
                </Button>
              </Grid>
            )}

            <Grid item className={classes.stepperContainer}>
              <MobileStepper
                sx={{
                  background: COLOR_FORM_BACKGROUND,
                  '& .MuiMobileStepper-dots': {
                    display: activeStep === maxSteps - 1 ? 'none' : 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                  '& .MuiMobileStepper-dot': {
                    background: '#fff',
                    margin: '0 5px',
                  },
                  '& .MuiMobileStepper-dotActive': {
                    width: 20,
                    height: 20,
                    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.25)',
                  },
                }}
                steps={4}
                position="static"
                variant={matches ? 'text' : 'dots'}
                activeStep={activeStepperStep}
                backButton={
                  activeStep !== 0 && (
                    <IconButton onClick={handleBack}>
                      {theme.direction === 'rtl' ? (
                        <KeyboardArrowRight style={{ fontSize: 40, color: '#fff' }} />
                      ) : (
                        <KeyboardArrowLeft style={{ fontSize: 40, color: '#fff' }} />
                      )}
                    </IconButton>
                  )
                }
                nextButton={<></>}
              />
            </Grid>
          </Grid>
        </div>
      </Grid>
    </Modal>
  );
};

export default SimulatorWizardModal;
